/**
 * External Link Handlers
 *
 * Utility functions for opening external links consistently across the app.
 * These functions provide a clean API and can be extended with WebView
 * detection and fallback handling in the future.
 */

import {
  SUPPORT_CONTACTS,
  EXTERNAL_URLS,
  SOCIAL_PLATFORMS,
} from '@/shared/constants/externalLinks';
import type { ExternalLinkOptions, ShareOptions, SocialPlatform } from './types';
import { openBettyChatWithLaunchStrategy } from './whatsappLaunch';

// =============================================================================
// CORE LINK HANDLER
// =============================================================================

/**
 * Open an external link in a new tab/window.
 *
 * @param url - The URL to open
 * @param options - Optional configuration
 */
export function openExternalLink(
  url: string,
  options: ExternalLinkOptions = {}
): void {
  if (typeof window === 'undefined') return;

  const { target = '_blank', windowFeatures = '' } = options;

  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `https://${url}`;

  try {
    window.open(absoluteUrl, target, windowFeatures);
  } catch {
    // Fallback: try navigating in same window if target was _self
    if (target === '_self') {
      window.location.href = absoluteUrl;
    }
    // Otherwise silently fail - user can try again
  }
}

// =============================================================================
// WHATSAPP
// =============================================================================

/**
 * Open WhatsApp chat with a specific number.
 *
 * @param phone - Phone number (with country code, no +)
 * @param message - Optional pre-filled message
 * @param options - Optional link options
 */
export function openWhatsApp(
  phone: string,
  message?: string,
  options?: ExternalLinkOptions
): void {
  const url = SOCIAL_PLATFORMS.WHATSAPP.chatUrl(phone, message);
  openExternalLink(url, options);
}

/**
 * Open WhatsApp chat with Betty (support).
 *
 * @param customMessage - Optional custom message (defaults to standard greeting)
 * @param options - Optional link options
 */
export function openBettyChat(
  customMessage?: string,
  options?: ExternalLinkOptions
): void {
  const message = customMessage || SUPPORT_CONTACTS.BETTY_DEFAULT_MESSAGE;
  openBettyChatWithLaunchStrategy({
    phone: SUPPORT_CONTACTS.BETTY_WHATSAPP,
    message,
    externalLinkOptions: options,
  });
}

// =============================================================================
// HELP CENTER
// =============================================================================

/**
 * Open the Help Center.
 *
 * @param articlePath - Optional path to a specific article
 * @param options - Optional link options
 */
export function openHelpCenter(
  articlePath?: string,
  options?: ExternalLinkOptions
): void {
  const url = articlePath
    ? `${EXTERNAL_URLS.HELP_CENTER}${articlePath}`
    : EXTERNAL_URLS.HELP_CENTER;
  openExternalLink(url, options);
}

/**
 * Open a specific Help Center article by key.
 *
 * @param articleKey - Key from EXTERNAL_URLS.HELP_CENTER_ARTICLES
 * @param options - Optional link options
 */
export function openHelpArticle(
  articleKey: keyof typeof EXTERNAL_URLS.HELP_CENTER_ARTICLES,
  options?: ExternalLinkOptions
): void {
  const url = EXTERNAL_URLS.HELP_CENTER_ARTICLES[articleKey];
  openExternalLink(url, options);
}

// =============================================================================
// SOCIAL SHARING
// =============================================================================

/**
 * Share content on a social platform.
 *
 * @param platform - The platform to share on
 * @param options - Share options (url, title, text)
 * @param linkOptions - Optional link options
 */
export async function shareOnPlatform(
  platform: SocialPlatform,
  options: ShareOptions,
  linkOptions?: ExternalLinkOptions
): Promise<void> {
  const { url, title, text } = options;

  // Try native share API first if requested
  if (platform === 'native' && typeof navigator !== 'undefined' && navigator.share) {
    try {
      await navigator.share({ url, title, text });
      return;
    } catch {
      // User cancelled or share failed - fall through silently
      return;
    }
  }

  // Platform-specific sharing
  let shareUrl: string;
  const shareText = text || url;

  switch (platform) {
    case 'whatsapp':
      shareUrl = SOCIAL_PLATFORMS.WHATSAPP.shareUrl(shareText);
      break;

    case 'facebook':
      shareUrl = SOCIAL_PLATFORMS.FACEBOOK.shareUrl(url);
      break;

    case 'twitter':
      shareUrl = SOCIAL_PLATFORMS.TWITTER.shareUrl(url, text);
      break;

    case 'telegram':
      shareUrl = SOCIAL_PLATFORMS.TELEGRAM.shareUrl(url, text);
      break;

    case 'sms':
      shareUrl = SOCIAL_PLATFORMS.SMS.shareUrl(shareText);
      break;

    default:
      // Unknown platform - silently return
      return;
  }

  openExternalLink(shareUrl, {
    windowFeatures: 'width=600,height=400,noopener,noreferrer',
    ...linkOptions,
  });
}

// =============================================================================
// ANCHOR TAG HELPERS
// =============================================================================

/**
 * Get props for an anchor tag that opens an external link.
 * Use this for <a> tags that need consistent external link behavior.
 *
 * @param url - The URL to open
 * @param options - Optional link options
 * @returns Props to spread on an anchor tag
 *
 * @example
 * <a {...getExternalLinkProps('https://example.com')}>Click me</a>
 */
export function getExternalLinkProps(
  url: string,
  options: ExternalLinkOptions = {}
): {
  href: string;
  target: string;
  rel: string;
  onClick: (e: React.MouseEvent) => void;
} {
  const { target = '_blank' } = options;

  return {
    href: url,
    target,
    rel: 'noopener noreferrer',
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      openExternalLink(url, options);
    },
  };
}
